<script setup>
/* global defineProps, defineEmits */
import { computed, useId } from "vue";
import {
  buildPresenceChangeRows,
  isResolvedPresenceRequest,
  presenceChangeDay,
  presenceRecordTime,
  presenceRecordType,
  presenceRequestKey,
} from "@/utils/presenceChanges";

const props = defineProps({
  day: String,
  modelValue: { type: Boolean, default: true },
  requests: { type: Object, default: () => ({}) },
  admin: Boolean,
  expanded: Boolean,
  decisions: { type: Object, default: () => ({}) },
  busy: Boolean,
});
const emit = defineEmits(["toggle", "decision", "day-decision"]);
const contentId = useId();
const dayLabel = computed(() => presenceChangeDay(props.day));
const rows = computed(() => buildPresenceChangeRows(props.requests));
const isHistory = computed(
  () =>
    rows.value.historyRows.length > 0 &&
    rows.value.historyRows.every((row) =>
      isResolvedPresenceRequest(row.request),
    ),
);
const panels = computed(() =>
  isHistory.value
    ? [{ title: "", rows: rows.value.historyRows, history: true }]
    : [
        { title: "Fichajes actuales", rows: rows.value.originalRows },
        {
          title: "Cambios solicitados",
          rows: rows.value.requestedRows,
          proposed: true,
        },
      ],
);
const accepted = (request) =>
  props.decisions[presenceRequestKey(request)] ?? props.modelValue;
const pendingRequests = computed(() =>
  (props.requests.workRecordRequests || []).filter(
    (request) => !isResolvedPresenceRequest(request),
  ),
);
const daySelection = computed(() => {
  const total = pendingRequests.value.length;
  const approved = pendingRequests.value.filter((request) =>
    accepted(request),
  ).length;
  return {
    approved,
    rejected: total - approved,
    allApproved: total > 0 && approved === total,
    allRejected: total > 0 && approved === 0,
  };
});
const selectDay = (option) => {
  if (props.admin && !props.busy && pendingRequests.value.length)
    emit("day-decision", option);
};
const kindLabel = (kind) =>
  ({
    new: "Nuevo",
    modification: "Modificado",
    cancelled: "Cancelado",
    unchanged: "Sin cambios",
  })[kind];
const statusLabel = (status) =>
  ({ approved: "Aprobada", rejected: "Rechazada", pending: "Pendiente" })[
    status
  ] || "Pendiente";
</script>

<template>
  <section class="presence-change-day" :class="{ 'is-expanded': expanded }">
    <h3 class="presence-change-day__heading">
      <button
        type="button"
        class="presence-change-day__toggle"
        :aria-expanded="expanded"
        :aria-controls="expanded ? contentId : undefined"
        @click="emit('toggle')"
      >
        <span>Día {{ dayLabel }}</span>
        <svg
          class="presence-change-day__chevron"
          :class="{ 'is-open': expanded }"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="m4 8 8 8 8-8" />
        </svg>
      </button>
    </h3>
    <div v-if="expanded" :id="contentId" class="presence-change-day__content">
      <div class="presence-change-panels" :class="{ 'is-history': isHistory }">
        <section
          v-for="panel in panels"
          :key="panel.proposed ? 'proposed' : 'current'"
          class="presence-change-panel"
          :aria-label="panel.history ? 'Histórico de cambios' : panel.title"
        >
          <div v-if="panel.title" class="presence-change-panel__heading">
            <h4 class="presence-change-panel__title">{{ panel.title }}</h4>
            <div
              v-if="panel.proposed && admin && pendingRequests.length"
              class="presence-change-day-actions"
              role="group"
              :aria-label="`Revisión del día ${dayLabel}`"
            >
              <button
                type="button"
                class="presence-change-day-action presence-change-day-action--reject"
                :aria-label="`Rechazar día ${dayLabel}: marcar todas las solicitudes pendientes`"
                title="Rechazar todas las solicitudes pendientes de este día"
                :aria-pressed="daySelection.allRejected"
                :disabled="busy"
                @click.stop="selectDay(false)"
              >
                <svg viewBox="0 0 20 20" aria-hidden="true">
                  <path d="m6 6 8 8M14 6l-8 8" />
                </svg>
              </button>
              <button
                type="button"
                class="presence-change-day-action presence-change-day-action--approve"
                :aria-label="`Aprobar día ${dayLabel}: marcar todas las solicitudes pendientes`"
                title="Aprobar todas las solicitudes pendientes de este día"
                :aria-pressed="daySelection.allApproved"
                :disabled="busy"
                @click.stop="selectDay(true)"
              >
                <svg viewBox="0 0 20 20" aria-hidden="true">
                  <path d="m5 10 3.5 3.5L15 7" />
                </svg>
              </button>
            </div>
          </div>
          <table
            v-if="panel.rows.length"
            class="presence-change-table"
            :class="{ 'has-actions': panel.proposed && admin }"
          >
            <caption class="visually-hidden">
              {{
                panel.history ? "Histórico de cambios" : panel.title
              }}
              del día
              {{
                dayLabel
              }}
            </caption>
            <colgroup>
              <col class="presence-change-table__type" />
              <col class="presence-change-table__time" />
              <col
                v-if="panel.history || (panel.proposed && admin)"
                class="presence-change-table__action"
              />
            </colgroup>
            <thead>
              <tr>
                <th scope="col">Tipo</th>
                <th scope="col">Hora</th>
                <th v-if="panel.history" scope="col">Estado</th>
                <th v-else-if="panel.proposed && admin" scope="col">
                  <span class="visually-hidden">Revisión</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in panel.rows" :key="row.key">
                <td>
                  <span class="presence-change-type"
                    ><span
                      v-if="panel.proposed || panel.history"
                      class="presence-change-dot"
                      :class="`is-${row.kind}`"
                      role="img"
                      :aria-label="kindLabel(row.kind)"
                    ></span
                    ><span>{{ presenceRecordType(row.type) }}</span></span
                  >
                </td>
                <td class="presence-change-table__hour">
                  <time>{{ presenceRecordTime(row.time) }}</time>
                </td>
                <td v-if="panel.history">
                  <span
                    class="presence-change-status"
                    :class="`is-${row.request.status}`"
                    >{{ statusLabel(row.request.status) }}</span
                  >
                </td>
                <td
                  v-else-if="panel.proposed && admin"
                  class="presence-change-table__review"
                >
                  <span
                    v-if="row.request && isResolvedPresenceRequest(row.request)"
                    class="presence-change-status"
                    :class="`is-${row.request.status}`"
                    >{{ statusLabel(row.request.status) }}</span
                  >
                  <div
                    v-else-if="row.request"
                    class="presence-change-decisions"
                  >
                    <button
                      type="button"
                      class="presence-change-decision presence-change-decision--reject"
                      :aria-pressed="!accepted(row.request)"
                      :aria-label="`Rechazar ${presenceRecordType(row.type)} de las ${presenceRecordTime(row.time)} del día ${dayLabel}`"
                      :disabled="busy"
                      @click.stop="
                        emit('decision', {
                          id: presenceRequestKey(row.request),
                          accepted: false,
                        })
                      "
                    >
                      <svg viewBox="0 0 20 20" aria-hidden="true">
                        <path d="m6 6 8 8M14 6l-8 8" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="presence-change-decision presence-change-decision--approve"
                      :aria-pressed="accepted(row.request)"
                      :aria-label="`Aprobar ${presenceRecordType(row.type)} de las ${presenceRecordTime(row.time)} del día ${dayLabel}`"
                      :disabled="busy"
                      @click.stop="
                        emit('decision', {
                          id: presenceRequestKey(row.request),
                          accepted: true,
                        })
                      "
                    >
                      <svg viewBox="0 0 20 20" aria-hidden="true">
                        <path d="m5 10 3.5 3.5L15 7" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-else class="presence-change-panel__empty">
            {{
              panel.proposed
                ? "No hay cambios solicitados."
                : "No hay fichajes registrados."
            }}
          </p>
          <p
            v-if="panel.proposed && admin && pendingRequests.length"
            class="presence-change-day-selection"
            role="status"
          >
            {{ daySelection.approved }} para aprobar ·
            {{ daySelection.rejected }} para rechazar
          </p>
        </section>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.presence-change-day {
  min-width: 0;
  overflow: hidden;
  border: 1px solid #c9c3ff;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.85);
}
.presence-change-day__heading {
  margin: 0;
}
.presence-change-day__toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: 42px;
  padding: 8px 16px;
  margin: 0;
  border: 0;
  background: transparent;
  -webkit-tap-highlight-color: transparent;
  color: $bg-blue;
  text-align: left;
  font-size: 20px;
  line-height: 1.25;
  font-weight: 800;
}
.presence-change-day__toggle > span {
  font: inherit;
}
// Keep the highlight tied to the accordion state on both touch and desktop.
.presence-change-day__toggle[aria-expanded="true"] {
  background: #f3f0ff;
}
.presence-change-day__toggle:focus-visible,
.presence-change-decision:focus-visible,
.presence-change-day-action:focus-visible {
  outline: 3px solid #409eff;
  outline-offset: -3px;
}
.presence-change-day__chevron {
  width: 23px;
  height: 23px;
  flex: 0 0 23px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.3;
  transition: transform 0.16s ease;
}
.presence-change-day__chevron.is-open {
  transform: rotate(180deg);
}
.presence-change-day__content {
  padding: 0 14px 12px;
}
.presence-change-panels {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.18fr);
  border-top: 1px solid #e1dcff;
  padding-top: 8px;
}
.presence-change-panels.is-history {
  grid-template-columns: minmax(0, 1fr);
}
.presence-change-panel {
  min-width: 0;
}
.presence-change-panel + .presence-change-panel {
  margin-left: 14px;
  padding-left: 14px;
  border-left: 1px solid #c9c3ff;
}
.presence-change-panel__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 36px;
  gap: 8px;
  margin-bottom: 5px;
}
.presence-change-panel__title {
  min-width: 0;
  margin: 0;
  color: $bg-blue;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.3;
}
.presence-change-day-actions {
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
}
.presence-change-day-selection {
  margin: 7px 0 0;
  font-size: 12px;
  line-height: 1.4;
  font-weight: 400;
  color: #606174;
}
.presence-change-panel__empty {
  margin: 12px 0;
  font-size: 14px;
  color: #606174;
}
.presence-change-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  color: #131528;
  font-size: 14px;
  line-height: 1.35;
}
.presence-change-table__time {
  width: 64px;
}
.presence-change-table__action {
  width: 105px;
}
.presence-change-table.has-actions .presence-change-table__action {
  width: 88px;
}
.presence-change-table th {
  font-weight: 800;
  padding: 3px 6px 6px;
  text-align: left;
}
.presence-change-table td {
  height: 34px;
  padding: 2px 6px;
  vertical-align: middle;
  overflow-wrap: anywhere;
}
.presence-change-table tbody tr:nth-child(odd) {
  background: #efedf6;
}
.presence-change-table td:first-child {
  border-radius: 5px 0 0 5px;
}
.presence-change-table td:last-child {
  border-radius: 0 5px 5px 0;
}
.presence-change-table__hour {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.presence-change-type {
  font: inherit;
  display: flex;
  align-items: center;
  gap: 7px;
}
.presence-change-type > span {
  font: inherit;
}
.presence-change-dot {
  width: 11px;
  height: 11px;
  flex: 0 0 11px;
  border-radius: 50%;
  background: #80c94b;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.05);
}
.presence-change-dot.is-modification {
  background: #f1d92c;
}
.presence-change-dot.is-cancelled {
  background: #fa1837;
}
.presence-change-dot.is-unchanged {
  background: #b5b4c3;
}
.presence-change-table__review .presence-change-status {
  min-width: 0;
  max-width: 100%;
  padding: 3px 4px;
  font-size: 11px;
}
.presence-change-decisions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.presence-change-decision,
.presence-change-day-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 32px;
  min-width: 32px;
  max-width: 32px;
  height: 32px;
  min-height: 32px;
  max-height: 32px;
  flex: 0 0 32px;
  padding: 0;
  margin: 0;
  border: 0;
  border-radius: 50%;
  appearance: none;
  cursor: pointer;
  vertical-align: middle;
  line-height: 1;
  opacity: 1;
  -webkit-tap-highlight-color: transparent;
}
.presence-change-decision svg,
.presence-change-day-action svg {
  display: block;
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  margin: 0;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  pointer-events: none;
}
.presence-change-decision--reject,
.presence-change-day-action--reject {
  --decision-background: #f78ab3;
}
.presence-change-decision--reject[aria-pressed="true"],
.presence-change-day-action--reject[aria-pressed="true"] {
  --decision-background: #f9156f;
}
.presence-change-decision--approve,
.presence-change-day-action--approve {
  --decision-background: #9295ee;
}
.presence-change-decision--approve[aria-pressed="true"],
.presence-change-day-action--approve[aria-pressed="true"] {
  --decision-background: #474ce6;
}
.presence-change-decision,
.presence-change-day-action,
.presence-change-day-action:hover,
.presence-change-day-action:focus,
.presence-change-day-action:focus-visible,
.presence-change-day-action:active,
.presence-change-decision:hover,
.presence-change-decision:focus,
.presence-change-decision:focus-visible,
.presence-change-decision:active {
  background: var(--decision-background);
  color: #fff;
  box-shadow: none;
}
.presence-change-decision:focus-visible,
.presence-change-day-action:focus-visible {
  outline: 3px solid #409eff;
  outline-offset: 2px;
}
.presence-change-decision:disabled,
.presence-change-day-action:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
.presence-change-status {
  display: inline-block;
  min-width: 88px;
  padding: 3px 9px;
  border-radius: 20px;
  background: #efebff;
  color: $bg-blue;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
}
.presence-change-status.is-approved {
  background: #c8eab7;
  color: #23620e;
}
.presence-change-status.is-rejected {
  background: #ffbfd3;
  color: #a4083a;
}
@media (max-width: 767px) {
  .presence-change-day__toggle {
    min-height: 44px;
    padding: 9px 12px;
    font-size: 18px;
  }
  .presence-change-day__content {
    padding: 0 10px 12px;
  }
  .presence-change-panel__heading {
    min-height: 44px;
  }
  .presence-change-panels {
    grid-template-columns: minmax(0, 1fr);
  }
  .presence-change-panel + .presence-change-panel {
    margin: 12px 0 0;
    padding: 10px 0 0;
    border-left: 0;
    border-top: 1px solid #e1dcff;
  }
  .presence-change-table {
    font-size: 13px;
    font-weight: 500;
  }
  .presence-change-table th {
    font-weight: 700;
  }
  .presence-change-table td {
    height: 40px;
  }
  .presence-change-decision,
  .presence-change-day-action {
    width: 36px;
    min-width: 36px;
    max-width: 36px;
    height: 36px;
    min-height: 36px;
    max-height: 36px;
    flex-basis: 36px;
  }
  .presence-change-table.has-actions .presence-change-table__action {
    width: 92px;
  }
}
@media (max-width: 359px) {
  .presence-change-day__toggle {
    font-size: 16px;
  }
  .presence-change-table th,
  .presence-change-table td {
    padding-left: 3px;
    padding-right: 3px;
  }
  .presence-change-type {
    gap: 4px;
  }
  .presence-change-table__time {
    width: 44px;
  }
  .presence-change-table__action {
    width: 88px;
  }
  .presence-change-status {
    min-width: 80px;
    padding: 3px 5px;
    font-size: 12px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .presence-change-day__chevron {
    transition: none;
  }
}
</style>
